import React, { useState } from "react";
import ReactLoading from "react-loading";
import { useParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { RoomCollection, RoomEntity } from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { assembleRoomGuest } from "@lifeat/shared/rooms/RoomUtil";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import LinkButton from "@common/LifeAtButton/Variants/LinkButton";
import LifeAtDropdownSelector from "@common/LifeAtDropdownSelector/LifeAtDropdownSelector";
import LifeAtDropdownSelectorElement from "@common/LifeAtDropdownSelector/LifeAtDropdownSelectorElement";
import { ModalNotification } from "@common/LifeAtModal/LifeAtModal";
import LifeAtTextInput from "@common/LifeAtTextInput/LifeAtTextInput";
import LifeAtToggle from "@common/LifeAtToggle/LifeAtToggle";
import { useUserContext } from "@contexts/UserContext";
import { functions } from "@firebase-local/firebase";
import { ROOMS_FUNCTIONS } from "@firebase-local/functions/ServerlessEndpoints";
import { useDbWithLocalCache } from "@hooks/useDbWithLocalCache";
import ChevronRightIcon from "@resources/icons/ChevronRightArrow.svg";
import Styles from "@themes/Styles";
import { redirectTo } from "@util/BrowserUtil";
import { removeAllGuestsFromRoom, setIsRoomSpaceSyncedStatus } from "../RoomService";
import { useRoomSettingsContext } from "../RoomSettingsContext";
import { RoomParams } from "../RoomTypes";
import { useAllRoomMetadataOwnedByUser } from "../hooks/useAllRoomMetadataOwnedByUser";
import { RoomSettingLabelPrimary, RoomSettingLabelSecondary } from "./RoomModalCommonElements";

const RoomSelectDropdown = styled(LifeAtDropdownSelector)`
    border: 1px solid ${({ theme }) => theme.borderPrimary};
    border-radius: 5px;
    align-self: flex-start;
    padding: 0 4px;
    & .dropdown-selector-preview {
        padding: 0;
    }
`;

const RoomSelectDropdownItem = styled.span<{ active: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    color: ${({ theme, active }) => (active ? theme.textColorSpecial : theme.textColorPrimary)};
`;

const StyledChevronRightIcon = styled(ChevronRightIcon)`
    cursor: pointer;
    fill: ${(props) => props.theme.iconColor};
`;

const RoomSettingsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
`;

const RoomSettingCol = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 20px;

    width: 100%;

    margin-top: 20px;
`;

const InputControl = styled.div`
    display: flex;
    flex-direction: column;
`;

const RoomSettingControls = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 20px 0;

    border-bottom: 1px solid ${(props) => props.theme.borderPrimary};
`;

const RoomSettingRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    padding-bottom: 15px;
`;

const RoomSettingTextInput = styled(LifeAtTextInput)`
    border: 1px solid ${(props) => props.theme.borderPrimary};
    border-radius: ${Styles.borderRadiusSharp};
`;

const RoomNameInputWrapper = styled.span<{ canEdit: boolean }>`
    min-height: 30px;
    height: auto;

    padding: 4px;

    border: 1px solid ${(props) => props.theme.borderPrimary};
    border-radius: ${Styles.borderRadiusSharp};

    cursor: ${(props) => (props.canEdit ? "pointer" : "not-allowed")};
    opacity: ${(props) => (props.canEdit ? 1 : 0.5)};
`;

const RemoveAllGuestsLinkBtn = styled(LinkButton)`
    margin-top: 10px;
    color: ${Styles.lifeatSpecialRed};
`;

const ROOM_SELECT_SETTING_LABEL = "Select Room";
const ADD_NEW_ROOM_LABEL = "Add new room";
const ADD_NEW_ROOM_ERROR_MESSAGE =
    "There was an error creating a new room. Please refresh and try again. If the issue continues to happen, please contact support.";
const ROOM_NAME_SETTING_LABEL = "Room Name";
const CANNOT_EDIT_ROOM_NAME_MSG = "Cannot edit Room Name while call is active";
const ROOM_NAME_EDIT_ERROR_MSG = "There was an error updating your Room name. Please try again or contact support.";

const ROOM_SETTING_GENERAL_CATEGORY_LABEL = "General";
const ROOM_SETTING_CREATOR_CATEGORY_LABEL = "Creator";
const ROOM_SETTING_GUESTS_CATEGORY_LABEL = "Guest Permissions";

const ROOM_LOCKED_SETTING_LABEL_PRIMARY = "Lock your room";

const ROOM_SPACE_SYNCED_SETTING_LABEL_PRIMARY = "Sync spaces for everyone";

const REMOVE_ALL_GUESTS_BTN_LABEL = "Remove all guests from room";
const REMOVE_ALL_GUESTS_CONFIRM_MSG =
    "This action will remove all current guests from your Room. " +
    "\n\nGuests will be able to rejoin once you refresh the app and only if your Room is unlocked. " +
    "\n\nPlease confirm to continue.";

const POMODORO_CHAT_FREEZE_SETTING_LABEL_PRIMARY = "Pause chat while Pomodoro is running";

export type RoomSettings = Pick<
    RoomEntity,
    "roomName" | "isPrivate" | "isSpaceSynced" | "liveStreaming" | "isHostInfoDisplayed" | "videoCallSettings"
>;

type RoomSettingsFormProps = {
    roomId: string;
    roomSettings: RoomSettings;
    setRoomSettings: React.Dispatch<React.SetStateAction<RoomSettings>>;
    isEditable: boolean;
    onError: (notification: ModalNotification) => void;
};

export function RoomSettingsForm(props: RoomSettingsFormProps) {
    const { roomId }: RoomParams = useParams();
    const { user, userData } = useUserContext();

    const allRoomMetadata = useAllRoomMetadataOwnedByUser(userData);
    const [roomNameSetting, setRoomNameSetting] = useState(props.roomSettings.roomName);
    const [isEditingRoomName, setIsEditingRoomName] = useState(false);

    const [isRoomLocked, setIsRoomLocked] = useState(props.roomSettings.isPrivate);
    const [isSpaceSyncedInput, setIsSpaceSyncedInput] = useState(props.roomSettings.isSpaceSynced);
    const [updatingRoomLockStatus, setUpdatingRoomLockStatus] = useState(false);

    const [freezeChatOnPomodoro, setFreezeChatOnPomodoro] = useDbWithLocalCache({
        node: RoomCollection.at(roomId).at("groupPomodoro").at("freezeChatOnPomodoro").node,
        initialValue: false,
    });

    const handleUpdateRoomName = (newName: string) => {
        if (props.isEditable) {
            // Update the rendered name in the input
            setRoomNameSetting(newName);
            // Then attempt to commit to DB
            functions
                .httpsCallable(ROOMS_FUNCTIONS.UpdateRoomName)({ roomId: props.roomId, newRoomName: newName })
                .then(() => {
                    props.setRoomSettings((original) => ({
                        ...original,
                        roomName: newName,
                    }));
                    setIsEditingRoomName(false);
                })
                .catch((error) => {
                    // If the operation failed, we revert to what was passed from the parent
                    // and show an error in the modal.
                    setRoomNameSetting(props.roomSettings.roomName);
                    props.onError({
                        type: "error",
                        message: error?.message || ROOM_NAME_EDIT_ERROR_MSG,
                    });
                });
        }
    };

    const handleEditRoomName = () => {
        if (props.isEditable) {
            setIsEditingRoomName(true);
        }
    };

    const handleToggleRoomLocked = () => {
        setUpdatingRoomLockStatus(true);
        functions
            .httpsCallable(ROOMS_FUNCTIONS.ToggleRoomLocked)({ roomId: props.roomId })
            .then(() => {
                setIsRoomLocked(!isRoomLocked);
                props.setRoomSettings((original) => ({
                    ...original,
                    isPrivate: !isRoomLocked,
                }));
                setUpdatingRoomLockStatus(false);
            })
            .catch(() => {
                setUpdatingRoomLockStatus(false);
            });
    };

    const handleToggleSyncGuestSpacesToHost = () => {
        const newStatus = !isSpaceSyncedInput;
        setIsRoomSpaceSyncedStatus(props.roomId, newStatus);
        props.setRoomSettings((original) => ({
            ...original,
            isSpaceSynced: newStatus,
        }));
        setIsSpaceSyncedInput(newStatus);
    };

    const handleRemoveAllGuests = () => {
        const doRemove = confirm(REMOVE_ALL_GUESTS_CONFIRM_MSG);
        if (doRemove) {
            const host = assembleRoomGuest(user.uid, props.roomId, userData);
            removeAllGuestsFromRoom(props.roomId, host);
        }
    };

    const handleRoomCreate = async () => {
        try {
            const { data: createdRoomId } = await functions.httpsCallable(ROOMS_FUNCTIONS.CreateRoom)();
            redirectTo(`/${Routes.Room}/${createdRoomId}`);
        } catch {
            props.onError({
                type: "error",
                message: ADD_NEW_ROOM_ERROR_MESSAGE,
            });
        }
    };

    const currentRoomSettings = (
        <RoomSettingCol className={`room-setting-col`}>
            <InputControl>
                <RoomSettingLabelSecondary className={`room-setting-label-primary`}>
                    {ROOM_SELECT_SETTING_LABEL}
                </RoomSettingLabelSecondary>
                <RoomSelectDropdown
                    selectedElementId={props.roomId}
                    selectedElementName={allRoomMetadata.find((metadata) => metadata.id === props.roomId)?.name ?? ""}
                >
                    {allRoomMetadata.map((metadata) => (
                        <LifeAtDropdownSelectorElement
                            key={metadata.id}
                            handleSelect={() => redirectTo(`/${Routes.Room}/${metadata.id}`)}
                        >
                            <RoomSelectDropdownItem active={props.roomId === metadata.id}>
                                {metadata.name}
                            </RoomSelectDropdownItem>
                        </LifeAtDropdownSelectorElement>
                    ))}
                </RoomSelectDropdown>
            </InputControl>
            <InputControl>
                <RoomSettingLabelSecondary className={`room-setting-label-primary`}>
                    {ROOM_NAME_SETTING_LABEL}
                </RoomSettingLabelSecondary>
                <RoomNameInputWrapper
                    className={`room-setting-input-wrapper`}
                    onClick={handleEditRoomName}
                    canEdit={props.isEditable}
                    data-tip={props.isEditable ? null : CANNOT_EDIT_ROOM_NAME_MSG}
                    data-for={`room-name-edit-call-running-tip`}
                >
                    <RoomSettingTextInput
                        textValue={roomNameSetting}
                        isEditing={isEditingRoomName}
                        placeholder={roomNameSetting}
                        finishEditCallback={() => setIsEditingRoomName(false)}
                        handleUpdate={handleUpdateRoomName}
                    />
                </RoomNameInputWrapper>
                {!props.isEditable && (
                    <ReactTooltip id={`room-name-edit-call-running-tip`} disable={props.isEditable} />
                )}
            </InputControl>
        </RoomSettingCol>
    );

    const generalRoomSettings = (
        <>
            {/* Room Locked Status */}
            <RoomSettingRow className={`room-setting-row`}>
                {updatingRoomLockStatus && <ReactLoading type={"spokes"} color={"#E39685"} height={30} width={30} />}
                {!updatingRoomLockStatus && (
                    <LifeAtToggle
                        className={`room-lock-settings-toggle`}
                        isEnabled={isRoomLocked}
                        onChange={handleToggleRoomLocked}
                    />
                )}
                <RoomSettingLabelPrimary className={`room-setting-label-primary`}>
                    {ROOM_LOCKED_SETTING_LABEL_PRIMARY}
                </RoomSettingLabelPrimary>
            </RoomSettingRow>

            {/* Sync Space Setting */}
            <RoomSettingRow className={`room-setting-row`}>
                <LifeAtToggle
                    className={`room-space-synced-settings-toggle`}
                    isEnabled={isSpaceSyncedInput}
                    onChange={handleToggleSyncGuestSpacesToHost}
                />

                <RoomSettingLabelPrimary className={`room-setting-label-primary`}>
                    {ROOM_SPACE_SYNCED_SETTING_LABEL_PRIMARY}
                </RoomSettingLabelPrimary>
            </RoomSettingRow>

            {/* Pause chat during pomodoro  */}
            <RoomSettingRow className={`room-setting-row`}>
                <LifeAtToggle
                    className={`room-space-synced-settings-toggle`}
                    isEnabled={freezeChatOnPomodoro}
                    onChange={() => setFreezeChatOnPomodoro(!freezeChatOnPomodoro)}
                />

                <RoomSettingLabelPrimary className={`room-setting-label-primary`}>
                    {POMODORO_CHAT_FREEZE_SETTING_LABEL_PRIMARY}
                </RoomSettingLabelPrimary>
            </RoomSettingRow>
        </>
    );

    const roomSettingEls = (
        <RoomSettingsWrapper
            key={`room-settings-modal-setting-elements`}
            className={`room-settings-modal-settings-els`}
        >
            {currentRoomSettings}

            <RoomSettingControls>
                <RoomSettingLabelSecondary className={`room-setting-label-primary`}>
                    {ROOM_SETTING_GENERAL_CATEGORY_LABEL}
                </RoomSettingLabelSecondary>
                {generalRoomSettings}

                <RoomSettingLabelSecondary className={`room-setting-label-primary`}>
                    {ROOM_SETTING_GUESTS_CATEGORY_LABEL}
                </RoomSettingLabelSecondary>
                {/* Room Video Call Settings */}
                <RoomVideoCallSettingsElements />
            </RoomSettingControls>

            {/* Host actions on guests */}

            <RemoveAllGuestsLinkBtn label={REMOVE_ALL_GUESTS_BTN_LABEL} handleClick={handleRemoveAllGuests} />
        </RoomSettingsWrapper>
    );

    return roomSettingEls;
}

const VIDEO_CALL_ENABLED_SETTING_LABEL = "Allow anyone to join video chat";
const WEBCAMS_ENABLED_SETTING_LABEL = "Allow anyone to turn on their webcam";
const MIC_ENABLED_SETTING_LABEL = "Allow anyone to unmute themselves";
const SCREEN_SHARE_ENABLED_SETTING_LABEL = "Allow anyone to share their screen";

function RoomVideoCallSettingsElements() {
    const {
        isVideoCallingDisabled,
        setVideoCallingDisabled,
        isGuestsCameraDisabled,
        setGuestsCameraDisabled,
        isGuestsMicDisabled,
        setGuestsMicDisabled,
        isScreenShareDisabled,
        setScreenShareDisabled,
    } = useRoomSettingsContext();

    return (
        <>
            {/* Video Call Disabled in General */}
            <RoomSettingRow className={`room-setting-row`}>
                <LifeAtToggle
                    className={`room-video-call-enabled-settings-toggle`}
                    isEnabled={!isVideoCallingDisabled}
                    onChange={() => setVideoCallingDisabled(!isVideoCallingDisabled)}
                />

                <RoomSettingLabelPrimary className={`room-setting-label-primary`}>
                    {VIDEO_CALL_ENABLED_SETTING_LABEL}
                </RoomSettingLabelPrimary>
            </RoomSettingRow>

            {/* Camera Disabled for All Guests */}
            <RoomSettingRow className={`room-setting-row`}>
                <LifeAtToggle
                    className={`room-video-call-guests-camera-settings-toggle`}
                    isEnabled={!isGuestsCameraDisabled}
                    onChange={() => setGuestsCameraDisabled(!isGuestsCameraDisabled)}
                    disabled={isVideoCallingDisabled}
                />

                <RoomSettingLabelPrimary className={`room-setting-label-primary`} isDisabled={isVideoCallingDisabled}>
                    {WEBCAMS_ENABLED_SETTING_LABEL}
                </RoomSettingLabelPrimary>
            </RoomSettingRow>

            {/* Mic Disabled for All Guests */}
            <RoomSettingRow className={`room-setting-row`}>
                <LifeAtToggle
                    className={`room-video-call-guests-mic-settings-toggle`}
                    isEnabled={!isGuestsMicDisabled}
                    onChange={() => setGuestsMicDisabled(!isGuestsMicDisabled)}
                    disabled={isVideoCallingDisabled}
                />

                <RoomSettingLabelPrimary className={`room-setting-label-primary`} isDisabled={isVideoCallingDisabled}>
                    {MIC_ENABLED_SETTING_LABEL}
                </RoomSettingLabelPrimary>
            </RoomSettingRow>

            {/* Screen share enablement */}
            <RoomSettingRow className={`room-setting-row`}>
                <LifeAtToggle
                    className={`room-video-call-screen-share-settings-toggle`}
                    isEnabled={!isScreenShareDisabled}
                    onChange={() => setScreenShareDisabled(!isScreenShareDisabled)}
                    disabled={isVideoCallingDisabled}
                />

                <RoomSettingLabelPrimary className={`room-setting-label-primary`} isDisabled={isVideoCallingDisabled}>
                    {SCREEN_SHARE_ENABLED_SETTING_LABEL}
                </RoomSettingLabelPrimary>
            </RoomSettingRow>
        </>
    );
}
